import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { INTERACTION_TYPES, LEAD_STATUSES, STATUS_CLASSES, STATUS_LABELS } from "@/lib/crmConstants";
import { Phone, Mail, Link2, CalendarClock, MessageSquarePlus, CheckCircle2, Trash2 } from "lucide-react";

const inputCls = "bg-black/30 border-white/15 text-white placeholder:text-white/30 rounded-xl focus-visible:ring-[color:var(--hs-focus)]";

export const LeadDetailPanel = ({ lead, onClose, onUpdated }) => {
  const [interactions, setInteractions] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [status, setStatus] = useState(lead?.status || "new");
  const [noteType, setNoteType] = useState("call");
  const [noteText, setNoteText] = useState("");
  const [fuTitle, setFuTitle] = useState("");
  const [fuDate, setFuDate] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!lead) return;
    try {
      const [ints, fus] = await Promise.all([
        api.get(`/leads/${lead.id}/interactions`),
        api.get("/followups", { params: { lead_id: lead.id } }),
      ]);
      setInteractions(ints.data.items);
      setFollowups(fus.data.items);
    } catch (e) {
      // silent
    }
  }, [lead]);

  useEffect(() => {
    setStatus(lead?.status || "new");
    setNoteText("");
    setFuTitle("");
    setFuDate("");
    load();
  }, [lead, load]);

  if (!lead) return null;

  const updateStatus = async (newStatus) => {
    setStatus(newStatus);
    try {
      await api.patch(`/leads/${lead.id}`, { status: newStatus });
      toast.success(`Status → ${STATUS_LABELS[newStatus]}`);
      onUpdated();
    } catch (e) {
      toast.error("Could not update status");
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) {
      toast.error("Write a note first");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/leads/${lead.id}/interactions`, { type: noteType, notes: noteText.trim() });
      setNoteText("");
      await load();
      toast.success("Interaction logged");
    } catch (e) {
      toast.error("Could not log interaction");
    } finally {
      setBusy(false);
    }
  };

  const addFollowup = async () => {
    if (!fuTitle.trim() || !fuDate) {
      toast.error("Follow-up needs a title and date");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/leads/${lead.id}/followups`, { title: fuTitle.trim(), due_date: fuDate });
      setFuTitle("");
      setFuDate("");
      await load();
      toast.success("Follow-up scheduled");
    } catch (e) {
      toast.error("Could not add follow-up");
    } finally {
      setBusy(false);
    }
  };

  const markDone = async (fu) => {
    try {
      await api.patch(`/followups/${fu.id}`, { status: fu.status === "done" ? "pending" : "done" });
      await load();
    } catch (e) {
      toast.error("Could not update follow-up");
    }
  };

  const deleteLead = async () => {
    if (!window.confirm(`Delete lead "${lead.name}"? This also removes its interactions and follow-ups.`)) return;
    try {
      await api.delete(`/leads/${lead.id}`);
      toast.success("Lead deleted");
      onClose();
      onUpdated();
    } catch (e) {
      toast.error("Could not delete lead");
    }
  };

  return (
    <Sheet open={Boolean(lead)} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="bg-[color:var(--hs-bg-1)] border-white/10 text-white w-full sm:max-w-lg overflow-y-auto" data-testid="crm-lead-detail-panel">
        <SheetHeader className="text-left">
          <div className="flex items-start justify-between gap-3 pr-8">
            <div>
              <SheetTitle className="font-display uppercase text-2xl text-white">{lead.name}</SheetTitle>
              <SheetDescription className="text-white/50">{lead.entity_type} · via {lead.source}</SheetDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={deleteLead} data-testid="crm-delete-lead-button" className="text-white/40 hover:text-[#ff7878] hover:bg-white/5 shrink-0">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </SheetHeader>

        <div className="mt-5 space-y-5">
          {/* Status */}
          <div>
            <label className="text-xs text-white/40 uppercase tracking-wider mb-2 block">Pipeline status</label>
            <Select value={status} onValueChange={updateStatus}>
              <SelectTrigger className={`${inputCls} h-11`} data-testid="crm-lead-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[color:var(--hs-bg-2)] border-white/15 text-white">
                {LEAD_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Contact info */}
          <div className="rounded-2xl border border-white/10 bg-black/25 p-4 space-y-2.5 text-sm">
            {lead.email && (
              <div className="flex items-center gap-2.5 text-white/80">
                <Mail className="h-4 w-4 text-[color:var(--hs-lime)] shrink-0" />
                <a href={`mailto:${lead.email}`} className="hover:text-[color:var(--hs-lime)] break-all">{lead.email}</a>
              </div>
            )}
            {lead.phone && (
              <div className="flex items-center gap-2.5 text-white/80">
                <Phone className="h-4 w-4 text-[color:var(--hs-lime)] shrink-0" />
                <a href={`tel:${lead.phone}`} className="hover:text-[color:var(--hs-lime)]">{lead.phone}</a>
              </div>
            )}
            {lead.links && (
              <div className="flex items-start gap-2.5 text-white/80">
                <Link2 className="h-4 w-4 text-[color:var(--hs-lime)] shrink-0 mt-0.5" />
                <span className="break-all whitespace-pre-wrap text-xs leading-relaxed">{lead.links}</span>
              </div>
            )}
            {lead.preferred_date && (
              <div className="flex items-center gap-2.5 text-white/80">
                <CalendarClock className="h-4 w-4 text-[color:var(--hs-lime)] shrink-0" />
                <span>Requested: {lead.preferred_date} · {lead.preferred_time}</span>
              </div>
            )}
            {lead.feedback && (
              <p className="text-xs text-white/50 border-t border-white/10 pt-2.5 whitespace-pre-wrap">“{lead.feedback}”</p>
            )}
            {(lead.utm_source || lead.utm_campaign) && (
              <p className="font-mono-ui text-[10px] text-white/35 border-t border-white/10 pt-2.5">
                UTM: {[lead.utm_source, lead.utm_medium, lead.utm_campaign].filter(Boolean).join(" / ")}
              </p>
            )}
          </div>

          {/* Interactions & follow-ups */}
          <Tabs defaultValue="interactions">
            <TabsList className="bg-black/30 border border-white/10 rounded-xl h-10 p-1 w-full">
              <TabsTrigger value="interactions" data-testid="crm-lead-tab-interactions" className="flex-1 rounded-lg data-[state=active]:bg-[color:var(--hs-lime)] data-[state=active]:text-[color:var(--hs-bg-0)] text-white/60 text-xs">
                Interactions ({interactions.length})
              </TabsTrigger>
              <TabsTrigger value="followups" data-testid="crm-lead-tab-followups" className="flex-1 rounded-lg data-[state=active]:bg-[color:var(--hs-lime)] data-[state=active]:text-[color:var(--hs-bg-0)] text-white/60 text-xs">
                Follow-ups ({followups.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="interactions" className="space-y-3 mt-4">
              <div className="flex gap-2">
                <Select value={noteType} onValueChange={setNoteType}>
                  <SelectTrigger className={`${inputCls} h-10 w-[120px] shrink-0`} data-testid="crm-note-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[color:var(--hs-bg-2)] border-white/15 text-white">
                    {INTERACTION_TYPES.map((t) => (
                      <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Textarea
                  data-testid="crm-note-input"
                  placeholder="Log a call, conversation or note…"
                  rows={1}
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  className={`${inputCls} resize-none min-h-[40px]`}
                />
              </div>
              <Button onClick={addNote} disabled={busy} data-testid="crm-lead-add-note-button" size="sm" className="bg-[color:var(--hs-lime)] text-[color:var(--hs-bg-0)] hover:bg-[color:var(--hs-lime-2)] font-semibold rounded-xl h-10 w-full">
                <MessageSquarePlus className="h-4 w-4 mr-2" /> Log Interaction
              </Button>
              <div className="space-y-2" data-testid="crm-interactions-list">
                {interactions.length === 0 && <p className="text-xs text-white/35 text-center py-3">No interactions logged yet.</p>}
                {interactions.map((int) => (
                  <div key={int.id} className="rounded-xl border border-white/10 bg-black/25 p-3">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <Badge variant="outline" className="text-[9px] uppercase tracking-wider bg-white/5 text-white/60 border-white/15">{int.type}</Badge>
                      <span className="font-mono-ui text-[10px] text-white/35">
                        {int.created_at?.slice(0, 10)} · {int.created_by}
                      </span>
                    </div>
                    <p className="text-sm text-white/80 whitespace-pre-wrap">{int.notes}</p>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="followups" className="space-y-3 mt-4">
              <div className="flex flex-col sm:flex-row gap-2">
                <Input
                  data-testid="crm-followup-title-input"
                  placeholder="e.g. Call to confirm consult slot"
                  value={fuTitle}
                  onChange={(e) => setFuTitle(e.target.value)}
                  className={`${inputCls} h-10 flex-1`}
                />
                <Input
                  data-testid="crm-followup-date-input"
                  type="date"
                  value={fuDate}
                  onChange={(e) => setFuDate(e.target.value)}
                  className={`${inputCls} h-10 sm:w-[150px] [color-scheme:dark]`}
                />
              </div>
              <Button onClick={addFollowup} disabled={busy} data-testid="crm-lead-add-followup-button" size="sm" className="bg-[color:var(--hs-lime)] text-[color:var(--hs-bg-0)] hover:bg-[color:var(--hs-lime-2)] font-semibold rounded-xl h-10 w-full">
                <CalendarClock className="h-4 w-4 mr-2" /> Schedule Follow-up
              </Button>
              <div className="space-y-2" data-testid="crm-followups-list">
                {followups.length === 0 && <p className="text-xs text-white/35 text-center py-3">No follow-ups for this lead.</p>}
                {followups.map((fu) => (
                  <div key={fu.id} className="flex items-center gap-3 rounded-xl border border-white/10 bg-black/25 p-3">
                    <button
                      onClick={() => markDone(fu)}
                      data-testid={`crm-followup-toggle-${fu.id}`}
                      className={`shrink-0 ${fu.status === "done" ? "text-[#78ffb4]" : "text-white/30 hover:text-[color:var(--hs-lime)]"}`}
                      aria-label="Toggle follow-up done"
                    >
                      <CheckCircle2 className="h-5 w-5" />
                    </button>
                    <div className="min-w-0 flex-1">
                      <p className={`text-sm ${fu.status === "done" ? "text-white/40 line-through" : "text-white/90"}`}>{fu.title}</p>
                      <p className="font-mono-ui text-[10px] text-white/35">Due {fu.due_date}</p>
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </SheetContent>
    </Sheet>
  );
};
